import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';

import { patchExternalConsentService } from '../../api/application';
import { getApplicationStateService } from '../../api/getApplicationState';
import { SubmitApplicationResponse, submitApplicationService } from '../../api/submitApplication';
import { submitCoApplicantService } from '../../api/submitCoApplicant';
import { ApplicationStateResponse } from '../../domain/types/applicationState';
import {
    selectConsentFieldStates,
    selectSubmittableFieldStates,
} from '../../store/applicationform/applicationFormSelectors';
import { IFieldState } from '../applicationform/applicationFormTypes';
import { stripUiOnlyChildFields } from '../applicationform/childAgeGroupUtil';
import { RootState } from '../store';
import { ApplicationState, CreateApplicationResponse, GetApplicationStateRequest } from './applicationTypes';

type IPatchConsentRequest = {
    applicationReferenceNumber: string;
    formFieldStates: IFieldState[];
};

const PARTNER = 'RESURS';

export const patchExternalConsentData = createAsyncThunk<void, IPatchConsentRequest, { state: RootState }>(
    'application/patchExternalConsentData',
    async (request, { getState }) => {
        const state = getState();
        return patchExternalConsentService({
            countryCode: state.settings.countryCode,
            languageCode: state.settings.languageCode,
            apiKey: state.settings.apiKey,
            proxyBaseUrl: state.settings.proxyBaseUrl,
            formFieldStates: request.formFieldStates,
            applicationReferenceNumber: request.applicationReferenceNumber,
            authenticationData: state.authentication?.authenticationData,
        }).then();
    },
);

export const submitApplication = createAsyncThunk<SubmitApplicationResponse, void, { state: RootState }>(
    'application/submitApplication',
    async (_, { getState }) => {
        const state = getState();
        const apiKey = state.settings.apiKey;

        if (!state.authentication || !state.authentication.authenticationData) {
            throw Error('Not authenticated');
        }

        // Get all form fields and append the channel
        const formFieldStates: IFieldState[] = stripUiOnlyChildFields([...selectSubmittableFieldStates(state)()]);

        const consentFieldStates: IFieldState[] = [...selectConsentFieldStates(state)()];
        return submitApplicationService(
            state.settings.proxyBaseUrl,
            state.settings.languageCode,
            state.settings.countryCode,
            apiKey,
            state.authentication.authenticationData,
            PARTNER,
            state.application.applicationId!,
            formFieldStates,
            consentFieldStates,
            state.form.consolidations,
            state.application.kycTemplateVersion,
        );
    },
);

type SubmitCoApplicantRequest = {
    applicationReferenceNumber: string;
};

export const submitCoApplicant = createAsyncThunk<any, SubmitCoApplicantRequest, { state: RootState }>(
    'application/submitCoApplication',
    async (request, { getState }) => {
        const state = getState();
        const apiKey = state.settings.apiKey;

        if (!state.authentication || !state.authentication.authenticationData) {
            throw Error('Not authenticated');
        }

        // Get all form fields and append the channel
        const formFieldStates: IFieldState[] = stripUiOnlyChildFields([...selectSubmittableFieldStates(state)()]);

        const consentFieldStates: IFieldState[] = [...selectConsentFieldStates(state)()];
        return submitCoApplicantService(
            state.settings.proxyBaseUrl,
            state.settings.languageCode,
            state.settings.countryCode,
            apiKey,
            state.authentication.authenticationData,
            state.application.applicationId!,
            formFieldStates,
            consentFieldStates,
            request.applicationReferenceNumber,
            state.form.consolidations,
            state.application.kycTemplateVersion,
        );
    },
);

export const getApplicationState = createAsyncThunk<
    ApplicationStateResponse,
    GetApplicationStateRequest,
    { state: RootState }
>('application/getApplicationState', async (request, { getState }) => {
    const state = getState();

    if (!request.applicationReferenceNumber) {
        return Promise.resolve({ state: undefined });
    }

    return await getApplicationStateService(
        state.settings.proxyBaseUrl,
        state.settings.apiKey,
        request.applicationReferenceNumber,
        state.authentication?.authenticationData,
    );
});

export const createNewApplication = createAsyncThunk<CreateApplicationResponse, void, { state: RootState }>(
    'application/createNewApplicationId',
    async (_) => {
        return { applicationId: uuid() };
    },
);

export const initialApplicationState = {
    applicationId: undefined,
    state: undefined,
    kycTemplateVersion: undefined,
} as ApplicationState;

export const applicationSlice = createSlice({
    name: 'application',
    initialState: initialApplicationState,
    reducers: {
        invalidateApplication: () => initialApplicationState,
        setKycTemplateVersion: (state, action: PayloadAction<number | undefined>) => {
            state.kycTemplateVersion = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(createNewApplication.fulfilled, (state, action) => {
            state.applicationId = action.payload.applicationId;
            // This slice is persisted, so without clearing it a new application would inherit the survey version of
            // the previous one. Every flow re-fetches a survey before it submits answers, so this is only a guard —
            // but a stale version is precisely what silently loses answers downstream.
            state.kycTemplateVersion = undefined;
        });
        builder.addCase(submitApplication.fulfilled, (state, _action) => {
            state.applicationId = undefined;
        });
        builder.addCase(submitCoApplicant.fulfilled, (state, _action) => {
            state.applicationId = undefined;
        });

        builder.addCase(getApplicationState.fulfilled, (state, action) => {
            state.state = action.payload.state;
        });
    },
});

export const { invalidateApplication, setKycTemplateVersion } = applicationSlice.actions;

export default applicationSlice.reducer;
